import React, { useEffect, useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { Col, Container, Row, Spinner, Form, Modal } from "react-bootstrap";
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';

import config from "config";
import sendRequest from "services/dataService";
import AuthenticationContext from "contexts/AuthenticationContext";

import { termsOfServiceRoute, codeOfConductRoute } from "routes/endpoints";

import "./Subscribe.scss";

// Asegurarse de que la clave pública de Stripe esté definida
if (!process.env.REACT_APP_STRIPE_PUBLIC_KEY) {
  console.error('REACT_APP_STRIPE_PUBLIC_KEY is not defined');
}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY!);

interface SubscribeFormData {
  coupon: string;
  terms_conditions: boolean;
  min_age: boolean;
  skip_trial: boolean;
}

const Subscribe = () => {
  const { userId } = useParams() as any;
  const invite_by = useSelector((state: any) => state.user);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<string>("Annual");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCheckout, setShowCheckout] = useState<boolean>(false);
  const [formData, setFormData] = useState<SubscribeFormData | null>(null);
  const authenticationContext = useContext(AuthenticationContext);
  const {
    register,
    handleSubmit,
    setError: setFormError,
    formState: { errors, isSubmitting },
  } = useForm<SubscribeFormData>();

  useEffect(() => {
    const url = `api/v1.0/user/${userId}/`;
    sendRequest({
      url,
      method: "GET",
      token: authenticationContext.token || "",
      body: null,
    }).then((response: any) => {
      setUser(response);
    });
  }, [userId, authenticationContext.token]);

  const handleCouponBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    const coupon = e.target.value;
    if (coupon) {
      setLoading(true);
      try {
        const response = await sendRequest({
          url: `api/v1.0/validate-coupon/`,
          method: "POST",
          token: authenticationContext.token || "",
          body: JSON.stringify({ coupon }),
        });
        if (response.valid) {
          toast.success("Coupon is valid!");
        } else {
          toast.error("Coupon is invalid.");
        }
      } catch (error) {
        toast.error("Failed to validate coupon. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePlanSelect = (plan: string) => {
    setSelectedPlan(plan);
  };

  const onSubmit = async (data: SubscribeFormData) => {
    try {
      setError(null);
      const response = await sendRequest({
        url: 'api/v1.0/payments/create-checkout-session/',
        method: 'POST',
        token: "",
        body: JSON.stringify({
          plan: selectedPlan,
          coupon: data.coupon,
          skipTrial: data.skip_trial,
          userId: userId
        }),
      });

      if (!response.checkoutSessionClientSecret?.client_secret) {
        throw new Error('No client secret received from server');
      }

      setClientSecret(response.checkoutSessionClientSecret.client_secret);
      setFormData(data);
      setShowCheckout(true);
    } catch (error: any) {
      console.error('Checkout error:', error);
      setError(error.message || 'Failed to initialize checkout. Please try again.');
      toast.error(error.message || 'Failed to initialize checkout. Please try again.');
    }
  };

  const handleCloseCheckout = () => {
    setShowCheckout(false);
    setClientSecret(null);
    setFormData(null);
  };

  return (
    <Container className="subscribe">
      <div className={`sign-up ${showCheckout ? 'show-checkout' : ''}`}>
        <h1>Payment subscription</h1>
        <div className="form-content">
          <div className="select-plan">
            <ul id="general_errors" className="errorlist" style={{ display: "none" }}></ul>
            <h3>Select the plan that works for you:</h3>
            <button
              type="button"
              className={`plan ${selectedPlan === "Quarterly" ? "selected" : ""}`}
              onClick={() => handlePlanSelect("Quarterly")}
            >
              <span className="ico-selected"></span>
              <h4>QUARTERLY</h4>
              <p>
                <div className="main-line" data-value="36.00" data-original="/ billed every 3 months">
                  <div className="price">$41</div>
                  / billed every 3 months
                </div>
              </p>
              <p className="amount">
                <div className="second-line" data-value="13.66" data-original="per month">
                  <div className="price">$13.66</div>
                  per month
                </div>
              </p>
            </button>
            <button
              type="button"
              className={`plan ${selectedPlan === "Annual" ? "selected" : ""}`}
              style={{ marginLeft: "1%" }}
              onClick={() => handlePlanSelect("Annual")}
            >
              <span className="ico-selected"></span>
              <h4>ANNUAL</h4>
              <p>
                <div className="main-line" data-value="99.00" data-original="/ billed annually">
                  <div className="price">$112</div>
                  / billed annually
                </div>
              </p>
              <p className="amount">
                <div className="second-line" data-value="9.33" data-original="per month">
                  <div className="price">$9.33</div>
                  per month (save 31.69%)
                </div>
              </p>
            </button>
            <input type="hidden" id="id_plan" value={selectedPlan} />
          </div>

          <Form onSubmit={handleSubmit(onSubmit)}>
            {user && !user.used_trial_period && (
              <div style={{ marginTop: "4%" }}>
                <p className="purple-text"> All plans come with a 14-day free trial.</p>
                <Row>
                  <Col md={3}>
                    <Form.Check 
                      type="switch"
                      id="skip-trial-switch"
                      label="Skip free trial"
                      {...register("skip_trial")}
                      className="custom-switch"
                    />
                  </Col>
                  <Col md={6} style={{ marginTop: "-1%" }}>
                    <p>Free trials allow you full access to the site review and critiquing platform. If you wish to access webinars or workshops, you must have a paid membership. You can elect to skip your free trial today for immediate access to webinars and workshops.</p>
                  </Col>
                </Row>
              </div>
            )}
            <div className="legal-terms">
              <Form.Group controlId="terms_conditions" className="terms_label">
                <Form.Check>
                  <Form.Check.Input 
                    type="checkbox"
                    {...register("terms_conditions", { 
                      required: "You must accept the terms and conditions." 
                    })}
                  />
                  <Form.Check.Label>
                    I have read and understand the <a href={termsOfServiceRoute} target="_blank" rel="noreferrer" className="blue-link">Terms, Conditions & Privacy Policy</a> and <a href={codeOfConductRoute} target="_blank" rel="noreferrer" className="blue-link">Code of Conduct</a>.
                  </Form.Check.Label>
                </Form.Check>
                {errors.terms_conditions && (
                  <Form.Text className="text-danger">{errors.terms_conditions.message}</Form.Text>
                )}
              </Form.Group>
              <br />

              <Form.Group controlId="min_age" className="terms_label">
                <Form.Check>
                  <Form.Check.Input 
                    type="checkbox"
                    {...register("min_age", { 
                      required: "You must be at least 13 years of age." 
                    })}
                  />
                  <Form.Check.Label>
                    I am at least 13 years of age.
                  </Form.Check.Label>
                </Form.Check>
                {errors.min_age && (
                  <Form.Text className="text-danger">{errors.min_age.message}</Form.Text>
                )}
              </Form.Group>
              <br />

              <Row>
                <Col md={6}>
                  <Form.Group controlId="coupon">
                    <Form.Label>Coupon code:</Form.Label>
                    <Form.Control
                      type="text"
                      {...register("coupon")}
                      onBlur={handleCouponBlur}
                    />
                  </Form.Group>
                </Col>
                <Col md={6} style={{ display: "flex", alignItems: "end" }}>
                  <div className="small_spinner_container">
                    {loading && <Spinner animation="border" role="status" />}
                  </div>
                </Col>
              </Row>
              <br />

              <ul id="invite_by_errors" className="errorlist" style={{ display: "none" }}></ul>
            </div>

            <p>After completing checkout, you will receive an email to activate your account.</p>

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            <button type="submit" className="btn btn-large" disabled={isSubmitting}>
              {isSubmitting ? "Processing..." : "CHECKOUT"}
            </button>
          </Form>
        </div>

        <div className="checkout-container">
          <button 
            className="btn btn-link back-button" 
            onClick={handleCloseCheckout}
          >
            ← Back to plan selection
          </button>
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{ clientSecret }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      </div>
    </Container>
  );
};

export default Subscribe;